import { createRouter, createWebHistory } from "vue-router";
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "@/firebase";
import { useConfigStore } from "@/stores/config";
import { useNotification } from "@/composables/useNotification";
import Home from "../views/Login.vue";

const routes = [
  {
    path: "/",
    name: "Home",
    component: Home,
    meta: { title: "Grupo del Valle Garbal" }
  },
  {
    path: "/admin",
    name: "Administrador",
    component: () => import("../views/Administrator.vue"),
    meta: { authRequired: true, adminRequired: true }
  },
  {
    path: "/dashboard/agebs",
    name: "AGEBs",
    component: () => import("../views/AGEBS.vue"),
    meta: { authRequired: true }
  },
  {
    path: "/dashboard/electoral",
    name: "Electoral",
    component: () => import("../views/ElectoralSections.vue"),
    meta: { authRequired: true }
  },
  {
    path: "/dashboard/index",
    name: "Indices",
    component: () => import("../views/Index.vue"),
    meta: { authRequired: true }
  },
  {
    path: "/survey",
    name: "Encuesta",
    component: () => import("../components/Survey/SurveyForm.vue")
  },
  {
    path: "/survey/admin",
    name: "SurveyAdmin",
    component: () => import("../components/Survey/SurveyAdmin.vue"),
    meta: { authRequired: true }
  }
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes
});

// Wait for Firebase Auth to initialize
function getCurrentUser() {
  return new Promise((resolve, reject) => {
    const unsubscribe = onAuthStateChanged(
      auth,
      (user) => {
        unsubscribe();
        resolve(user);
      },
      reject
    );
  });
}

// Check whether the current user is an admin (fresh from Firestore, not
// trusted client-side state) via the user doc's `admin` flag or the
// config store's adminEmails allowlist.
async function checkIsAdmin(user) {
  try {
    const userDoc = await getDoc(doc(db, "users", user.uid));
    if (userDoc.exists() && userDoc.data().admin === true) {
      return true;
    }
  } catch {
    // fall through to email allowlist check
  }

  const configStore = useConfigStore();
  await configStore.fetchConfig();
  return configStore.isAdmin(user.email);
}

router.beforeEach(async (to, from, next) => {
  if (to.matched.some((record) => record.meta.authRequired)) {
    const user = await getCurrentUser();
    if (!user) {
      useNotification().warning("Debes iniciar sesión para ver esta página");
      next({ path: "/" });
      return;
    }

    if (to.matched.some((record) => record.meta.adminRequired)) {
      const isAdmin = await checkIsAdmin(user);
      if (!isAdmin) {
        useNotification().error("Debes ser administrador para ver esta página");
        next({ path: "/" });
        return;
      }
    }

    next();
  } else {
    next();
  }
});

router.afterEach((to) => {
  document.title = to.meta.title || to.name || "Grupo del Valle Garbal";
});

export default router;
